/**
 * @file message-links.ts
 * @description Разбор текста сообщения на куски с ссылками и порог
 * «длинного» сообщения (chat-window-design.md §3).
 *
 * Знак препинания в конце адреса («смотрите https://site.ru/a.») — часть
 * фразы, а не адреса: в ссылку не входит. Закрывающая скобка остаётся в
 * адресе, только если в нём есть парная открывающая (адреса вида
 * `wiki/Чай_(напиток)`). Свёрнутое сообщение режется ПОСЛЕ разбора ссылок:
 * ссылка не обрывается на середине адреса.
 */

export const LONG_MESSAGE_CHARS = 1_500;
const URL_RE = /https?:\/\/[^\s<>"]+/g;
/** Знаки, которые в конце адреса относятся к фразе. */
const TRAILING_PUNCT = new Set([".", ",", ";", ":", "!", "?", "…", "»", "\"", "'", "”", "’"]);
const PAIRS: Record<string, string> = { ")": "(", "]": "[", "}": "{" };

export interface MessagePart {
  text: string;
  href?: string;
}

function count(text: string, ch: string): number {
  let n = 0;
  for (const c of text) if (c === ch) n += 1;
  return n;
}

/** Отрезать от найденного адреса хвостовые знаки препинания фразы. */
export function trimUrlTail(raw: string): { url: string; tail: string } {
  let url = raw;
  for (;;) {
    const last = url.charAt(url.length - 1);
    const open = PAIRS[last];
    const unpaired = open !== undefined && count(url, open) < count(url, last);
    if (!TRAILING_PUNCT.has(last) && !unpaired) break;
    url = url.slice(0, -1);
  }
  return { url, tail: raw.slice(url.length) };
}

/** Куски текста: обычный текст и ссылки. */
export function splitLinks(text: string): MessagePart[] {
  const parts: MessagePart[] = [];
  let plain = "";
  let from = 0;
  for (const match of text.matchAll(URL_RE)) {
    const start = match.index ?? 0;
    const { url, tail } = trimUrlTail(match[0]);
    plain += text.slice(from, start);
    from = start + match[0].length;
    // Адрес из одной схемы («https://.») — просто текст.
    if (/^https?:\/\/.+/.test(url)) {
      if (plain) parts.push({ text: plain });
      parts.push({ text: url, href: url });
      plain = tail;
    } else {
      plain += match[0];
    }
  }
  plain += text.slice(from);
  if (plain) parts.push({ text: plain });
  return parts;
}

/**
 * Свернуть разобранные куски до `limit` знаков. Текст режется по границе,
 * ссылка — целиком: начавшаяся до границы показывается полностью, после —
 * не показывается.
 */
export function clipParts(parts: MessagePart[], limit: number): { parts: MessagePart[]; clipped: boolean } {
  const out: MessagePart[] = [];
  let used = 0;
  for (const part of parts) {
    if (used >= limit) return { parts: out, clipped: true };
    const room = limit - used;
    if (part.href || part.text.length <= room) {
      out.push(part);
      used += part.text.length;
      continue;
    }
    out.push({ text: part.text.slice(0, room) });
    return { parts: out, clipped: true };
  }
  return { parts: out, clipped: false };
}
